// crewbit-admin/src/components/money/T4AVendorsCard.tsx
//
// Vendor list paid > $500 YTD with totals (T4A reporting threshold). Admin
// variant of mobile's T4AVendorsCard. v1 hidden by default per §3 — single
// tax_type 'hst' is hardcoded by the parent; T4A surface arrives via Generate
// Summary's per-vendor breakdown when the accountant pulls a year-end summary.
//
// Renders only when given a non-empty vendors list (parent decides
// visibility). Read-only display.
//
// Spec §Tax.7.2 + §Tax.D5.

import { Users } from 'lucide-react';
import type { T4AVendorRow } from '../../hooks/useTaxSummaryDetail';
import { formatMoney } from '../../lib/money';
import { palette, admin, spacing, shadow } from '../../styles/tokens';

interface T4AVendorsCardProps {
  vendors: T4AVendorRow[] | null;
}

export function T4AVendorsCard({ vendors }: T4AVendorsCardProps) {
  if (!vendors || vendors.length === 0) return null;

  return (
    <div
      style={{
        background: palette.white,
        border: `1px solid ${palette.g150}`,
        borderRadius: admin.cardRadius,
        padding: spacing.md,
        boxShadow: shadow.xs,
        display: 'flex',
        flexDirection: 'column',
        gap: spacing.xs,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs, marginBottom: spacing.xs }}>
        <Users size={15} color={palette.g600} strokeWidth={2} />
        <div style={{ fontSize: 13.5, fontWeight: 600, color: admin.header, flex: 1 }}>
          T4A-eligible vendors
        </div>
        <div style={{ fontSize: 11.5, color: palette.g500, fontVariantNumeric: 'tabular-nums' }}>
          {vendors.length} {vendors.length === 1 ? 'vendor' : 'vendors'}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {vendors.map((v, idx) => (
          <div
            key={v.vendor_id}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: spacing.sm,
              padding: `${spacing.sm}px 0`,
              borderTop: idx === 0 ? 'none' : `1px solid ${palette.g100}`,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: admin.header,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {v.vendor_name ?? 'Unnamed vendor'}
              </div>
              {v.business_number && (
                <div style={{ fontSize: 11.5, color: palette.g500, fontVariantNumeric: 'tabular-nums' }}>
                  {v.business_number}
                </div>
              )}
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: admin.header,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatMoney(v.ytd_total)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
